import React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { RoomCollection } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import useDbValueListener from "@hooks/useDbValueListener";
import Styles from "@themes/Styles";
import { useRoomController } from "../../RoomController";
import { RoomParams } from "../../RoomTypes";
import { ChatContextType, useChatContext } from "../ChatContext";
import { ChatMessagesContextProvider } from "../ChatMessagesContext";
import RoomChatCard from "./RoomChatCard";
import RoomChatFooter from "./RoomChatFooter";
import RoomChatHeader from "./RoomChatHeader";
import RoomChatMessagesContent from "./RoomChatMessagesContent";

const RoomChatWrapper = styled.div<{ show: boolean; fullHeight: boolean }>`
    position: fixed;
    display: flex;

    justify-content: space-between;
    height: calc(
        100% - ${Styles.distanceFromSidesOfWindow} * 2 -
            ${(props) => (props.fullHeight ? `0px` : Styles.topBarAndPaddingHeight)}
    );
    margin: ${Styles.distanceFromSidesOfWindow};
    margin-top: ${(props) =>
        props.fullHeight
            ? `${Styles.distanceFromSidesOfWindow}`
            : `calc(${Styles.topBarAndPaddingHeight} + ${Styles.distanceFromSidesOfWindow})`};
    width: auto;

    -webkit-transition: all 0.3s ease-out;
    -moz-transition: all 0.3s ease-out;
    -ms-transition: all 0.3s ease-out;
    -o-transition: all 0.3s ease-out;
    transition: all 0.3s ease-out;

    right: ${(props: { show: boolean }) =>
        props.show ? "0" : `calc(-${Styles.distanceFromSidesOfWindow} - ${Styles.sidebarWidth})`};
`;

const RoomChatContent = styled.div`
    display: flex;
    flex-direction: column;

    height: 100%;
    width: 300px;

    border-radius: ${Styles.widgetBorderRadius};

    color: ${(props) => props.theme.textColorPrimary};
    background-color: ${(props) => props.theme.backgroundSpecial};
`;

const RoomChatBody = styled.div`
    display: flex;
    flex-direction: column;

    height: 100%;
    width: 100%;

    overflow: auto;
`;

const RoomChatComponentWrapper = styled.div<{ topComponent: boolean }>`
    display: flex;
    flex-direction: column;
    flex-grow: ${(props) => (props.topComponent ? `0` : `1`)};

    overflow: ${(props) => (props.topComponent ? `unset` : `auto`)};

    border-radius: ${Styles.widgetBorderRadius};

    margin: 5px;
    background-color: ${(props) => props.theme.backgroundPrimary};
`;

interface RoomChatSidebarProps {}
export default function RoomChatSidebar(props: RoomChatSidebarProps) {
    const { roomId }: RoomParams = useParams();

    const { isChatOpen }: ChatContextType = useChatContext();
    const { isFocusModeActive } = useAppearanceContext();

    const isHostInfoDisplayed = useDbValueListener(RoomCollection.at(roomId).at("isHostInfoDisplayed").node);

    const roomController = useRoomController();

    const roomChatContent = (
        <RoomChatBody>
            {isHostInfoDisplayed && roomController.roomInfo?.ownerUid && (
                <RoomChatComponentWrapper topComponent={true}>
                    <RoomChatCard roomOwnerUid={roomController.roomInfo.ownerUid} />
                </RoomChatComponentWrapper>
            )}
            <RoomChatComponentWrapper topComponent={false}>
                <RoomChatMessagesContent
                    roomInfo={roomController.roomInfo}
                    isInteractingBlocked={roomController.isInteractingBlocked}
                />
                <RoomChatFooter isInteractingBlocked={roomController.isInteractingBlocked} />
            </RoomChatComponentWrapper>
        </RoomChatBody>
    );

    return (
        <ChatMessagesContextProvider>
            <RoomChatWrapper className={`rooms-chat-wrapper`} show={isChatOpen} fullHeight={isFocusModeActive ?? false}>
                <RoomChatContent>
                    <RoomChatHeader />
                    {roomChatContent}
                </RoomChatContent>
            </RoomChatWrapper>
        </ChatMessagesContextProvider>
    );
}
